import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/contexts/auth-context";
import { API_BASE } from "@/config/api";
import { Loader2, AlertCircle, User, AtSign } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

import {
  editProfileFormSchema,
  type EditProfileFormData,
} from "@/schemas/user.schema";

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
}

export function EditProfileModal({ open, onClose }: EditProfileModalProps) {
  const { user, updateUser } = useAuth();

  const form = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileFormSchema),
    defaultValues: {
      name: "",
      username: "",
    },
  });

  const {
    formState: { isSubmitting, errors },
    setError,
  } = form;

  // Initialize form with current user data
  useEffect(() => {
    if (open && user) {
      form.reset({
        name: user.name || "",
        username: user.username || "",
      });
    }
  }, [open, user, form]);

  const onSubmit = async (data: EditProfileFormData) => {
    if (!user) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/users/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name.trim(),
          username: data.username?.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || errorData.message || "Failed to update profile"
        );
      }

      const responseData = await response.json();
      // Backend returns { user }
      if (responseData.user) {
        updateUser(responseData.user);
      }
      onClose();
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("root", {
        message:
          err instanceof Error ? err.message : "Failed to update profile",
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Remove @ if user types it
  const handleUsernameChange = (
    value: string,
    onChange: (value: string) => void
  ) => {
    const cleanValue = value.replace(/^@/, "");
    onChange(cleanValue);
  };

  const usernameValue = form.watch("username");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update your name and username. Your username must be unique.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4 py-4">
              {errors.root && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Full Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          className="pl-9"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Username{" "}
                      <span className="text-muted-foreground text-xs">
                        (optional)
                      </span>
                    </FormLabel>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <FormControl>
                        <Input
                          placeholder="johndoe"
                          className="pl-9"
                          disabled={isSubmitting}
                          {...field}
                          onChange={(e) =>
                            handleUsernameChange(e.target.value, field.onChange)
                          }
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                    {usernameValue && !errors.username && (
                      <p className="text-sm text-muted-foreground">
                        Your profile will be @{usernameValue}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
